// Sticky buttons scroll behavior
export function initStickyButtons() {
    const stickyButtons = Array.from(document.querySelectorAll('.sticky-buttons'));
    if (!stickyButtons.length) return;
    const fixedMoveDuration = 1000;
    const leaveEndHandlers = new WeakMap();
    const enterEndHandlers = new WeakMap();
    const returnEndHandlers = new WeakMap();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const preHeaderHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--pre-header-height')
    ) || 26;

    const adminBarEl = document.getElementById('wpadminbar');
    const adminBarHeight = adminBarEl ? adminBarEl.offsetHeight : 0;

    function getFixedTopPosition() {
        // Position where element lands when fixed (10vh + adjustments)
        return (window.innerHeight * 0.1) + preHeaderHeight + adminBarHeight;
    }

    let triggers = [];

    function calculateTriggers() {
        const fixedTopPosition = getFixedTopPosition();

        triggers = stickyButtons.map((el) => {
            // getBoundingClientRect() includes transform: translateY(-50%), so this is
            // the rendered top edge — works for any page regardless of hero height.
            const initialRect = el.getBoundingClientRect();
            const elementVisualDocumentTop = initialRect.top + window.scrollY;

            // Trigger scroll: the point at which the element's visual top reaches fixedTopPosition
            return elementVisualDocumentTop - fixedTopPosition;
        });
    }

    function removeEnterEndHandler(el) {
        const handler = enterEndHandlers.get(el);
        if (handler) {
            el.removeEventListener('transitionend', handler);
            enterEndHandlers.delete(el);
        }
    }

    function removeLeaveEndHandler(el) {
        const handler = leaveEndHandlers.get(el);
        if (handler) {
            el.removeEventListener('animationend', handler);
            leaveEndHandlers.delete(el);
        }
    }

    function removeReturnEndHandler(el) {
        const handler = returnEndHandlers.get(el);
        if (handler) {
            el.removeEventListener('animationend', handler);
            returnEndHandlers.delete(el);
        }
    }

    function clearEnterAnimation(el) {
        removeEnterEndHandler(el);

        el.classList.remove('is-entering');
        el.style.removeProperty('transition');
        el.style.removeProperty('opacity');
        el.style.removeProperty('transform');
    }

    function animateEnterToFixed(el) {
        if (prefersReducedMotion) {
            el.classList.add('is-fixed');
            return;
        }

        const fromRect = el.getBoundingClientRect();
        el.classList.add('is-fixed');
        const toRect = el.getBoundingClientRect();
        const deltaY = fromRect.top - toRect.top;

        if (Math.abs(deltaY) < 1) {
            return;
        }

        clearEnterAnimation(el);
        el.classList.add('is-entering');
        el.style.transform = `translateY(${deltaY}px)`;
        el.style.opacity = '0.9';

        const onTransitionEnd = (event) => {
            if (event.propertyName !== 'transform') return;
            clearEnterAnimation(el);
        };

        removeEnterEndHandler(el);
        enterEndHandlers.set(el, onTransitionEnd);
        el.addEventListener('transitionend', onTransitionEnd);

        requestAnimationFrame(() => {
            el.style.transition = `transform ${fixedMoveDuration}ms cubic-bezier(0.16, 1, 0.3, 1), opacity ${fixedMoveDuration}ms ease-out`;
            el.style.transform = 'translateY(0)';
            el.style.opacity = '1';
        });
    }

    function setFixedState(el, shouldBeFixed) {
        if (shouldBeFixed) {
            removeLeaveEndHandler(el);
            removeReturnEndHandler(el);
            el.classList.remove('is-leaving', 'is-returning');

            if (el.classList.contains('is-fixed')) {
                return;
            }

            animateEnterToFixed(el);
            return;
        }

        if (!el.classList.contains('is-fixed')) {
            return;
        }

        if (el.classList.contains('is-leaving')) {
            return;
        }

        clearEnterAnimation(el);
        el.classList.add('is-leaving');

        const onAnimationEnd = (event) => {
            if (event.animationName !== 'sticky-buttons-fade-out') return;

            el.classList.remove('is-fixed', 'is-leaving');
            removeLeaveEndHandler(el);

            if (prefersReducedMotion) {
                return;
            }

            const onReturnAnimationEnd = (returnEvent) => {
                if (returnEvent.animationName !== 'sticky-buttons-return-fade-in') return;

                el.classList.remove('is-returning');
                removeReturnEndHandler(el);
            };

            removeReturnEndHandler(el);
            returnEndHandlers.set(el, onReturnAnimationEnd);
            el.classList.add('is-returning');
            el.addEventListener('animationend', onReturnAnimationEnd);
        };

        removeLeaveEndHandler(el);
        leaveEndHandlers.set(el, onAnimationEnd);
        el.addEventListener('animationend', onAnimationEnd);
    }

    function updatePosition() {
        stickyButtons.forEach((el, index) => {
            setFixedState(el, window.scrollY >= triggers[index]);
        });
    }

    calculateTriggers();

    const lenis = window.lenisInstance;
    if (lenis) {
        lenis.on('scroll', updatePosition);
    } else {
        window.addEventListener('scroll', updatePosition, { passive: true });
    }

    window.addEventListener('resize', () => {
        calculateTriggers();
        updatePosition();
    });

    updatePosition();


}
